import React, { useState, useEffect } from 'react';
import { X, Cake, PartyPopper, Sparkles, Gift, Heart } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import Confetti from './Confetti';
import { checkBirthdays } from '../lib/bots';

export const BirthdayCelebration: React.FC = () => {
  const { profile } = useAuthStore();
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!profile?.birthday_month || !profile?.birthday_day) return;
    
    const today = new Date();
    const isBirthday = 
      today.getMonth() + 1 === profile.birthday_month && 
      today.getDate() === profile.birthday_day;
    
    // Check if already dismissed today
    const dismissedKey = `birthday_dismissed_${today.toDateString()}`;
    const wasDismissed = localStorage.getItem(dismissedKey);
    
    if (isBirthday && !wasDismissed) {
      setShow(true);
      // Trigger birthday notification + bonus points via the bot system
      checkBirthdays().catch(console.error);
    }
  }, [profile]);

  const handleDismiss = () => {
    const today = new Date();
    localStorage.setItem(`birthday_dismissed_${today.toDateString()}`, 'true');
    setDismissed(true);
    setTimeout(() => setShow(false), 500);
  };

  if (!show) return null;

  return (
    <>
      {/* Full page confetti */}
      <Confetti pieceCount={150} duration={8000} />
      
      {/* Birthday Banner */}
      <div 
        className={`fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm transition-opacity duration-500 ${dismissed ? 'opacity-0' : 'opacity-100'}`}
        onClick={handleDismiss}
      >
        <div 
          className="relative max-w-lg mx-4 p-8 bg-gradient-to-br from-pink-900/90 via-purple-900/90 to-indigo-900/90 rounded-3xl border-2 border-pink-500/50 shadow-2xl shadow-pink-500/20 animate-bounce-slow"
          onClick={e => e.stopPropagation()}
        >
          {/* Floating decorations */}
          <div className="absolute -top-6 -left-6 text-5xl animate-bounce">🎈</div>
          <div className="absolute -top-4 -right-4 text-5xl animate-bounce" style={{ animationDelay: '0.2s' }}>🎂</div>
          <div className="absolute -bottom-4 -left-4 text-4xl animate-bounce" style={{ animationDelay: '0.4s' }}>🎁</div>
          <div className="absolute -bottom-6 -right-6 text-4xl animate-bounce" style={{ animationDelay: '0.6s' }}>🎉</div>
          
          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 p-2 text-white/60 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
          
          {/* Main content */}
          <div className="text-center">
            <div className="flex justify-center gap-2 mb-4">
              <PartyPopper className="w-10 h-10 text-yellow-400 animate-pulse" />
              <Cake className="w-12 h-12 text-pink-400" />
              <PartyPopper className="w-10 h-10 text-yellow-400 animate-pulse" style={{ transform: 'scaleX(-1)' }} />
            </div>
            
            <h1 className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-300 via-purple-300 to-indigo-300 mb-2">
              🎉 HAPPY BIRTHDAY! 🎉
            </h1>
            
            <p className="text-2xl font-bold text-white mb-4">
              {profile?.full_name || profile?.username}!
            </p>
            
            <div className="space-y-3 text-lg text-pink-100/90">
              <p className="flex items-center justify-center gap-2">
                <Sparkles className="w-5 h-5 text-yellow-400" />
                The entire AUDC family wishes you an amazing day!
                <Sparkles className="w-5 h-5 text-yellow-400" />
              </p>
              <p className="text-pink-200/80 italic">
                May your code compile on the first try! 💻✨
              </p>
            </div>
            
            <div className="mt-6 flex items-center justify-center gap-4">
              <Gift className="w-6 h-6 text-green-400" />
              <span className="text-lg font-medium text-green-300">
                +25 Birthday Bonus Points! 🎁
              </span>
              <Gift className="w-6 h-6 text-green-400" />
            </div>
            
            <button
              onClick={handleDismiss}
              className="mt-6 px-8 py-3 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-400 hover:to-purple-400 text-white font-bold rounded-full transition-all transform hover:scale-105 shadow-lg shadow-pink-500/30"
            >
              <span className="flex items-center gap-2">
                <Heart className="w-5 h-5" />
                Thank you!
                <Heart className="w-5 h-5" />
              </span>
            </button>
          </div>
        </div>
      </div>
      
      <style>{`
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }
      `}</style>
    </>
  );
};

export default BirthdayCelebration;

